import React from 'react';
import { Wrench, MessageSquare, AlertTriangle, DollarSign } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { companyInfo } from '../data/mock';

const CustomerCareSupportPolicyPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <section className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="heading-1 mb-4">Customer Care & Support Policy</h1>
            <p className="text-[#B5D43B] font-medium italic mb-8">Applies after project handover</p>
            
            <div className="prose prose-gray max-w-none space-y-8">
              <p className="body-large">
                At Supa Energy Hub, we're committed to supporting our customers even after everything has been delivered. This Customer Care & Support Policy outlines what you can expect <strong>after handover</strong>, including how we handle support requests, warranties, and troubleshooting.
              </p>
              
              <div className="bg-[#B5D43B]/10 rounded-xl p-6 border border-[#B5D43B]/20">
                <p className="text-[#3D5A47] font-medium">
                  <strong>"Handover"</strong> refers to the point where your system or service has been fully delivered — including installation, commissioning, compliance documentation, and grid connection (if applicable). At this stage, there are no further deliverables owed under the original agreement.
                </p>
              </div>

              {/* What's Included */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <Wrench className="w-6 h-6 text-[#B5D43B]" />
                  What's Included
                </h2>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Warranty Support</strong>
                      <span className="text-gray-600"> – Assistance with manufacturer warranty claims for eligible products we supplied and installed.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Monitoring App Assistance</strong>
                      <span className="text-gray-600"> – Help with inverter or battery app setup, if supported by the product.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">Service Request Handling</strong>
                      <span className="text-gray-600"> – Faults or concerns are reviewed within <strong>1–2 business days</strong> by our support team.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#B5D43B] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong className="text-[#3D5A47]">System Troubleshooting</strong>
                      <span className="text-gray-600"> – Guidance for any post-handover performance concerns, where covered under warranty.</span>
                    </div>
                  </li>
                </ul>
              </section>

              {/* Continued Commitment */}
              <section>
                <h2 className="heading-3 mb-4">🧑‍🔧 Continued Commitment</h2>
                <p className="text-gray-600 leading-relaxed">
                  Even after handover, our goal is to make sure your system runs reliably and that you feel supported. While the formal scope of our agreement may be complete, our care continues through clear, accessible support and fair troubleshooting.
                </p>
              </section>

              {/* Official Channels */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-[#B5D43B]" />
                  Official Customer Support Channels
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  To ensure your enquiry is tracked and resolved properly, please use our official channels only:
                </p>
                <div className="bg-white rounded-xl p-6 border border-black/5 space-y-3">
                  <p className="flex items-center gap-3 text-[#3D5A47] font-medium">
                    <span className="text-xl">📧</span>
                    hello@supaenergyhub.com.au
                  </p>
                  <p className="flex items-center gap-3 text-[#3D5A47] font-medium">
                    <span className="text-xl">📞</span>
                    {companyInfo.phone}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 mt-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Please do not contact individual team members directly</strong>, such as Sales Consultants, Operations staff, or Installers, via their mobile numbers or personal emails. These are not part of our official support system, and doing so may result in delays or missed service.
                  </p>
                </div>
                <p className="text-gray-600 leading-relaxed mt-4">Using our official channels ensures:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mt-2">
                  <li>Proper tracking of your request</li>
                  <li>Timely assignment to the right team</li>
                  <li>A consistent, high-quality support experience</li>
                </ul>
              </section>

              {/* Not Covered */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                  Not Covered Under Free Support
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>Issues caused by <strong>shading</strong>, <strong>weather</strong>, or <strong>changes in electricity usage</strong></li>
                  <li>Damage or faults due to <strong>unauthorised third-party modifications</strong></li>
                  <li><strong>Internet or Wi-Fi issues</strong> affecting inverter or battery monitoring apps</li>
                  <li>Support requests made <strong>after the warranty period</strong> has expired</li>
                  <li>Troubleshooting for <strong>external issues</strong> not caused by Supa Energy Hub's installation or products</li>
                </ul>
              </section>

              {/* Service Fees */}
              <section>
                <h2 className="heading-3 mb-4 flex items-center gap-2">
                  <DollarSign className="w-6 h-6 text-[#B5D43B]" />
                  Technician Time & Service Call-Out Fees
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  If support is required after handover and the issue is <strong>not covered under warranty</strong>, the following fees may apply:
                </p>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#3D5A47] mt-2 flex-shrink-0"></div>
                    <span><strong>On-Site Call-Out & Labour Charges</strong> – If a technician is required to attend your property.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#3D5A47] mt-2 flex-shrink-0"></div>
                    <div>
                      <strong>Chargeable Phone Support</strong> – If a technician needs to spend time over the phone to diagnose or assist with an issue that:
                      <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li>Is unrelated to the original product/service we supplied,</li>
                        <li>Is caused by external factors (e.g., modem upgrades, Wi-Fi instability, user error),</li>
                        <li>Or is the result of third-party modifications or interference.</li>
                      </ul>
                    </div>
                  </li>
                </ul>
                <p className="text-[#3D5A47] font-medium mt-4">
                  We will <strong>always notify you in advance</strong> if any charges will apply before proceeding.
                </p>
                <p className="text-gray-600 leading-relaxed mt-4">
                  We value your time — and we also value our technicians' expertise. This structure allows us to maintain fairness and consistency in supporting all our customers.
                </p>
              </section>

              {/* Need Support */}
              <section className="pt-8 border-t border-black/10">
                <h2 className="heading-3 mb-4">💬 Need Support?</h2>
                <p className="text-gray-600 leading-relaxed mb-4">When contacting us, please include:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 mb-6">
                  <li>Your <strong>job number</strong></li>
                  <li>Your <strong>property address</strong></li>
                  <li>A brief <strong>description of the issue</strong></li>
                </ul>
                <div className="bg-white rounded-xl p-6 border border-black/5 space-y-2">
                  <p className="flex items-center gap-3 text-[#3D5A47]">
                    <span>📧</span>
                    <strong>hello@supaenergyhub.com.au</strong>
                  </p>
                  <p className="flex items-center gap-3 text-[#3D5A47]">
                    <span>📞</span>
                    <strong>{companyInfo.phone}</strong>
                  </p>
                  <p className="flex items-center gap-3 text-[#3D5A47]">
                    <span>🌐</span>
                    <strong>supaenergyhub.com.au</strong>
                  </p>
                </div>
              </section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CustomerCareSupportPolicyPage;
